import { useMessage, usePublicSiteStatus } from '@/providers';
import { combineErrorStacks, isPS, logger } from '@/utils';
import {
  type MutationFunctionContext,
  type QueryFunctionContext,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult as UseQueryResultRQ,
  useIsMutating as useIsMutatingRQ,
  useMutation as useMutationRQ,
  useQuery as useQueryRQ,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { ZodError } from 'zod';
import { ApiErrorCodeLabelModifier } from './error-codes';

export type UseQueryResult<TData = unknown, TError = unknown> = Omit<
  UseQueryResultRQ<TData, TError>,
  'isLoading'
>;

export const useMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult> = {},
  skipMessage?: boolean,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> => {
  const { openMessage } = useMessage();
  const { t } = useTranslation();
  const mutationFn = options.mutationFn;
  const originalErr = new Error();
  return useMutationRQ({
    ...options,
    ...(mutationFn && {
      mutationFn: async (
        variables: TVariables,
        context: MutationFunctionContext,
      ): Promise<TData> => {
        try {
          return await mutationFn(variables, context);
        } catch (error) {
          if (!skipMessage && isAxiosError(error)) {
            const respondedServer = error.response?.headers.Server;
            if (
              error.response?.status === 403 &&
              respondedServer === 'awselb/2.0'
            ) {
              await openMessage(
                'error',
                t('error-message-unreached-api-server'),
              );
            } else {
              const errorCode = error.response?.data.error_code;

              if (errorCode) {
                await openMessage(
                  'error',
                  ApiErrorCodeLabelModifier[errorCode] ??
                    t('error-message-unknown-error'),
                );
              } else {
                await openMessage('error', t('error-message-unknown-error'));
              }
            }
          }
          throw error;
        }
      },
    }),
    onError: (error) => {
      const err = combineErrorStacks(error, originalErr);
      const data = { options, skipMessage };
      if (isAxiosError(err) && err.response) {
        const msg = `failed to useMutation. ${err.config?.method?.toUpperCase()} ${err.config?.url}`;
        if (err.response.status < 500) {
          logger.warn(msg, data, err);
        } else {
          logger.error(msg, err, data);
        }
      } else if (err instanceof ZodError) {
        logger.error(`failed to parse. ${options.mutationKey?.[0]}`, err, data);
      } else {
        logger.error(
          `failed to useMutation. ${options.mutationKey?.[0]}`,
          err,
          data,
        );
      }
      throw err;
    },
  });
};

export const useQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> => {
  const originalErr = new Error();
  const queryFn =
    typeof options.queryFn === 'function' ? options.queryFn : undefined;

  // パブリックサイトでのみusePublicSiteStatusを使用
  const publicSiteStatus = isPS() ? usePublicSiteStatus() : null;

  return useQueryRQ({
    ...options,
    ...(queryFn && {
      queryFn: async (context: QueryFunctionContext<TQueryKey>) => {
        try {
          return await queryFn(context);
        } catch (error) {
          const err = combineErrorStacks(error, originalErr);
          const data = { options, context };
          if (isAxiosError(err) && err.response) {
            const msg = `failed to useQuery. ${err.config?.method?.toUpperCase()} ${err.config?.url}`;
            if (err.response.status < 500) {
              logger.warn(msg, data, err);
            } else {
              logger.error(msg, err, data);
            }
            // パブリックサイトでのみAxiosエラーを処理
            if (publicSiteStatus) {
              publicSiteStatus.handleAxiosError(err);
            }
          } else if (err instanceof ZodError) {
            logger.error(
              `failed to parse. ${options.queryKey?.[0]}`,
              err,
              data,
            );
          } else {
            logger.error(
              `failed to useQuery. ${options.queryKey?.[0]}`,
              err,
              data,
            );
          }
          throw err;
        }
      },
    }),
  });
};
export const useIsMutating = useIsMutatingRQ;
